import type { Game, Player, Member, AvailableActionOption } from '../../types/game'
import { Worker } from '../shared/Worker'
import { ResourceIcon } from '../shared/ResourceIcon'
import { PlayerMarker } from '../shared/PlayerMarker'
import { FlowerCard } from './FlowerCard'
import { assetUrl } from '../../utils/assets'

interface CurrentPlayerTableauProps {
  game: Game
  player: Player
  member: Member | null
  availableActions: AvailableActionOption[]
  animatedWorkerDelta?: number
  onPass: () => void
  onPollinate: (flowerId: number) => void
  onOpenDonate: () => void
}

const RESOURCES = ['nectar', 'pollen', 'honey', 'wax', 'propolis'] as const

export function CurrentPlayerTableau({
  game,
  player,
  member,
  availableActions,
  animatedWorkerDelta = 0,
  onPass,
  onPollinate,
  onOpenDonate,
}: CurrentPlayerTableauProps) {
  const isMyTurn = game.current_player.color === player.color
  const canDonate = game.donation_mode !== 'disabled'
  const flowerPlaceholders = Math.max(0, player.max_flowers - player.flowers.length)
  const displayedRemainingWorkers = Math.max(0, player.n_remaining_workers - animatedWorkerDelta)

  return (
    <div
      className={`current-player-tableau tableau ${isMyTurn ? 'turn' : ''}`}
      style={{ '--icon-bg': `var(--${player.color})` } as React.CSSProperties}
    >
      <div className="current-player-header">
        <div className="current-player-identity">
          <PlayerMarker color={player.color} className="current-player-marker" />
          <span className="current-player-title">You</span>
          {player.evolutions.some((evolution) => evolution.type === 'increased_wing_strength') && (
            <span
              className={`wing-strength-badge${player.has_used_wing_strength ? ' used' : ''}`}
              title={player.has_used_wing_strength
                ? 'You have already pollinated a Flower twice this year.'
                : 'You can pollinate a Flower twice this year.'}
            >
              x2
            </span>
          )}
          {member?.is_host && (
            <img
              src={assetUrl('images', 'host.svg')}
              alt="Host"
              className="host-icon"
            />
          )}
        </div>
      </div>

      <div className="current-player-token-pool-container">
        <div className="current-player-token-pool-header">
          <span className="current-player-title">Workers ({displayedRemainingWorkers}/{player.n_workers})</span>
          <div className="player-action-buttons">
            <div className="pass" onClick={onPass}>Pass</div>
            {canDonate && (
              <div className="pass" onClick={onOpenDonate}>Donate</div>
            )}
          </div>
        </div>
        <div className="current-player-token-pool" data-worker-source={`player-${player.color}`}>
          {Array.from({ length: displayedRemainingWorkers }).map((_, i) => (
            <Worker key={i} color={player.color} size="1.4em" />
          ))}
        </div>
      </div>

      <div className="current-player-token-pool-container">
        <span className="current-player-title">Resources</span>
        <div className="current-player-resources">
          <div className="resource-pair">
            <ResourceIcon resource="egg" size="1.4em" />
            <span>{player.n_eggs}</span>
          </div>
          {RESOURCES.map((res) => {
            const amount = player.resources[res] ?? 0
            return (
              <div key={res} className="resource-pair">
                <ResourceIcon resource={res} size="1.4em" />
                <span>
                  {amount}
                  {res === 'honey' && player.required_honey !== undefined && (
                    <span
                      className="honey-required-hint"
                      title="Honey needed to feed your Workers at the Cold of Winter"
                    >
                      {' '}({player.required_honey})
                    </span>
                  )}
                </span>
              </div>
            )
          })}
        </div>
      </div>

      <div className="current-player-flower-container">
        <span className="current-player-title">Pollinate</span>
        <div className="player-flowers">
          {player.flowers.map((flower) => (
            <FlowerCard
              key={flower.id}
              flower={flower}
              currentSeason={game.current_season}
              onClick={() => onPollinate(flower.id)}
              workerColor={
                player.pollinated_flowers.some((pollinatedFlower) => pollinatedFlower.id === flower.id)
                  ? player.color
                  : undefined
              }
              selfPollinationSpent={player.spent_self_pollination_flower_ids?.includes(flower.id)}
            />
          ))}
          {Array.from({ length: flowerPlaceholders }).map((_, index) => (
            <div key={`placeholder-${index}`} className="flower-placeholder" />
          ))}
        </div>
      </div>
    </div>
  )
}
